import { useState } from "react";
import { postMessageFeedback } from "duck/endpoints/messageFeedback";
import { useChatContext } from "duck/ui/chat/contexts/useChatContext";
import { ChatMessage } from "duck/ui/chat/types";
import { useTranslation } from "react-i18next";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";

import Button from "features/components/Button";
import IconButton from "features/components/IconButton";
import TextField from "features/components/TextField";

interface MessageFeedbackProps {
  message: ChatMessage;
  isHovered?: boolean;
  isLastAIMessage?: boolean;
  threadId?: string; // override for subthreads
}

const MessageFeedback = ({
  message,
  isHovered = false,
  isLastAIMessage = false,
  threadId: threadIdProp,
}: MessageFeedbackProps) => {
  const { t } = useTranslation("duck");
  const { currentThreadId } = useChatContext();
  const threadId = threadIdProp ?? currentThreadId;
  const [submitting, setSubmitting] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<boolean | null>(
    null
  );
  const [showCommentInput, setShowCommentInput] = useState<boolean>(false);
  const [comment, setComment] = useState("");

  const handleFeedbackClick = async (positiveFeedback: boolean) => {
    if (!threadId || submitting) return;

    setSubmitting(true);
    try {
      await postMessageFeedback({
        threadId,
        messageId: message.serverId ?? message.id,
        positiveFeedback,
      });
      setSelectedFeedback(positiveFeedback);
      setShowCommentInput(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCommentSubmit = async () => {
    if (!threadId || !comment.trim() || selectedFeedback === null) return;

    setSubmitting(true);
    try {
      await postMessageFeedback({
        threadId,
        messageId: message.serverId ?? message.id,
        positiveFeedback: selectedFeedback,
        comment: comment.trim(),
      });
      setComment("");
      setShowCommentInput(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (showCommentInput) {
    return (
      <div className="flex justify-end gap-2">
        <TextField
          placeholder={t("add_comment_placeholder")}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          disabled={submitting}
          className="flex-1"
          testId="feedback-comment-input"
        />
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={handleCommentSubmit}
          disabled={submitting || !comment.trim()}
          testId="feedback-comment-submit"
        >
          {t("submit")}
        </Button>
        <Button
          size="small"
          variant="text"
          onClick={() => setShowCommentInput(false)}
          disabled={submitting}
          testId="feedback-comment-cancel"
        >
          {t("cancel")}
        </Button>
      </div>
    );
  }

  if (!isHovered && !isLastAIMessage) {
    return null;
  }

  return (
    <div className="flex justify-end -mt-3 gap-1">
      <IconButton
        size="small"
        onClick={() => handleFeedbackClick(true)}
        disabled={submitting}
        color={selectedFeedback === true ? "success" : undefined}
        sx={
          !selectedFeedback
            ? { "&:hover": { color: "success.main" } }
            : undefined
        }
        testId="feedback-thumbs-up"
      >
        <ThumbUpAltOutlinedIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => handleFeedbackClick(false)}
        disabled={submitting}
        color={selectedFeedback === false ? "error" : undefined}
        sx={
          selectedFeedback !== false
            ? { "&:hover": { color: "error.main" } }
            : undefined
        }
        testId="feedback-thumbs-down"
      >
        <ThumbDownAltOutlinedIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

export default MessageFeedback;
